import type { RouteObject } from "react-router-dom";
import { lazy } from "react";
import { addSlashes } from "../route.utils";
import { Spinner } from "@/components/ui/v2/spinner";
import { MySpaceCompanyPoliciesSummary } from "@/pages/(home)/(my-space)/company-policy/_components/ms-company-policies-summary";
import { store } from "@/store";
import { MSCompanyPoliciesApi } from "@/services/(my-space)/api.company-policies";

// Lazy so the route file doesn't eagerly pull the tabs page (which imports
// nav-tabs.config) during config initialization — avoids a circular-import TDZ.
const MySpaceCompanyPoliciesPage = lazy(() =>
  import("@/pages/(home)/(my-space)/company-policy").then((m) => ({
    default: m.MySpaceCompanyPoliciesPage,
  })),
);
const MySpaceCompanyPoliciesListPage = lazy(
  () => import("@/pages/(home)/(my-space)/company-policy/list"),
);

export const MS_DYNAMIC_COMPANY_POLICIES_ID_KEY = "companyPoliciesId";
export enum MS_COMPANY_POLICIES_PATH_SEGMENTS {
  ROOT = "company-policies",
  DETAILS = `:${MS_DYNAMIC_COMPANY_POLICIES_ID_KEY}`,
  EDIT = "edit",
}

export const myspaceCompanyPoliciesRoutes: RouteObject = {
  path: MS_COMPANY_POLICIES_PATH_SEGMENTS.ROOT,
  handle: { breadcrumb: "Company Policies" },
  HydrateFallback: Spinner,
  children: [
    // ── Page-level tabs layout (single "Company Policies" tab at the root) ──
    {
      Component: MySpaceCompanyPoliciesPage,
      handle: { flushContent: true },
      children: [{ index: true, Component: MySpaceCompanyPoliciesListPage }],
    },
    {
      path: MS_COMPANY_POLICIES_PATH_SEGMENTS.DETAILS,
      loader: async ({ params }) => {
        const companyPoliciesId = params[MS_DYNAMIC_COMPANY_POLICIES_ID_KEY]!;
        const result = await store.dispatch(
          MSCompanyPoliciesApi.endpoints.getCompanyPolicyById.initiate({ companyPoliciesId }),
        );
        const name = result.data?.data?.title ?? `Policy #${companyPoliciesId}`;
        return { breadcrumbLabel: name };
      },
      handle: {
        breadcrumb: (data: { breadcrumbLabel: string }) => data?.breadcrumbLabel ?? "Policy",
      },
      children: [
        {
          index: true,
          Component: MySpaceCompanyPoliciesSummary,
        },
      ],
    },
  ],
};

export const MS_COMPANY_POLICIES_PATHS = Object.freeze({
  ROOT: addSlashes(MS_COMPANY_POLICIES_PATH_SEGMENTS.ROOT),
  VIEW: (id: string | number) => addSlashes(MS_COMPANY_POLICIES_PATH_SEGMENTS.ROOT, String(id)),
});
